import { TableDrawer } from "@components/TableDrawer/TableDrawer";
import { useQuery } from "@hooks/useQuery";
import { decodeHTML } from "@utils/CommonUtils";
import { drilldownData, ModuleConfig } from "@utils/drilldownData";
import { Pagination, TableColumnsType, Typography } from "antd";
import { get as _get } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { DataTable } from "~/Components";
import ConfirmationModal from "~/Components/Modal/confirmation-modal";
import { useNotification } from "~/Hooks/useNotification";
import { eramanCMSApi, post, remove } from "~/Services";
import { eramanHost, magpHost } from "~/Utils";
import ActionDropdown, { TypeData } from "~/Utils/ActionDropdown";
import { renderActionsColumn, renderDateColumn, renderPublishedColumnStatus } from "~/Utils/TableRenderFunction";
import { useFaqs } from "../../Pages.hook";

export const Faqs = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [id, setId] = useState<string | undefined>(selectedRowData?.id);

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);

  const [searchValue, setSearchValue] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);

  const [data, setData] = useState<any>([]);
  const [sortedData, setSortedData] = useState<any>([]);
  const currentPageData = sortedData.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
  const [sortOrder, setSortOrder] = useState<string | undefined>();
  const [orderByName, setOrderByName] = useState<string>("id");
  const [selectedPublished, setSelectedPublished] = useState<any>("");

  const publishedData: TypeData[] = [
    { label: "Yes", value: true },
    { label: "No", value: false },
  ];

  const { data: drilldown, error, isLoading: drilldownLoading, isError, refetch: refetchDocument } = useFaqs(id || "");

  useEffect(() => {
    if (id) {
      refetchDocument();
    }
  }, [id]);

  useEffect(() => {
    const safeData = Array.isArray(data) ? data : [];
    let filteredData = [...safeData];

    if (searchValue) {
      const searchLower = decodeHTML(searchValue).toLowerCase();
      filteredData = filteredData.filter((item: any) =>
        decodeHTML(String(item.title || ""))
          .toLowerCase()
          .includes(searchLower)
      );
    }

    if (selectedPublished !== "") {
      filteredData = filteredData.filter((item: any) => {
        const publishedValue = String(item.published).toLowerCase() === "true";
        return publishedValue === selectedPublished;
      });
    }

    setSortedData(filteredData);
    setTotalRecords(filteredData.length);
    setCurrentPage(0);
  }, [data, searchValue, selectedPublished]);

  const getRecords = () => {
    setIsLoading(true);
    let url = `/api/faqs/all-fields`;
    eramanCMSApi
      .get(url)
      .then((res: any) => {
        if (res && res.data) {
          let envelopes = res.data;
          setTotalRecords(envelopes.length);
          setData(envelopes);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        setData([]);
        setIsLoading(false);
      });
  };

  const handleDelete = async (id: string) => {
    remove(`${magpHost}/api/faq-details/${id}`)
      .then((res) => {
        getRecords();
      })
      .catch((err) => {});
  };

  const columns: TableColumnsType = [
    {
      title: "S. No",
      dataIndex: "field_sequence",
      key: "field_sequence",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "field_sequence"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "field_sequence"
            ? "descend"
            : undefined,
    },
    {
      title: "Question",
      dataIndex: "title",
      key: "title",
      render: (value: any) => decodeHTML(value),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "title"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "title"
            ? "descend"
            : undefined,
    },
    {
      title: "Published",
      dataIndex: "published",
      key: "published",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "published"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "published"
            ? "descend"
            : undefined,
      render: renderPublishedColumnStatus,
    },
    {
      title: "Created At",
      dataIndex: "field_created_date",
      key: "field_created_date",
      fixed: "right",
      render: (value: any) => renderDateColumn(value),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "field_created_date"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "field_created_date"
            ? "descend"
            : undefined,
    },
    {
      title: "Updated At",
      dataIndex: "field_updated_date",
      key: "field_updated_date",
      fixed: "right",
      // render: (value: any) => renderDateColumn(value),
      render: (value: any, record: any) => {
        const created = record?.field_created_date
          ? moment(record.field_created_date).format("YYYY-MM-DD HH:mm:ss")
          : null;
        const updated = value ? moment(value).format("YYYY-MM-DD HH:mm:ss") : null;

        // if both same -> show null for updated
        if (created && updated && created === updated) {
          return null; // you can also return "-" or <Typography.Text>-</Typography.Text>
        }

        return renderDateColumn(value);
      },
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "field_updated_date"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "field_updated_date"
            ? "descend"
            : undefined,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      render: (record: any) =>
        renderActionsColumn(
          record,
          setSelectedRowData,
          setDrawerVisible,
          (e: any) => {
            e.preventDefault();
            navigate(`/dedicated/eraman/pages/eramanPrivilegeCard/Faqs/add?editId=${record.id}`);
          },
          undefined,
          setDeleteModalVisible,
          "users"
        ),
    },
  ];

  useEffect(() => {
    getRecords();
  }, []);

  useEffect(() => {
    setId(selectedRowData?.id);
  }, [selectedRowData]);

  useEffect(() => {
    setSelectedPublished("");
    setSearchValue("");
    setCurrentPage(0);
  }, []);

  const handlePublishedChange = (value: any) => {
    setSelectedPublished(value);
    setCurrentPage(0);
  };

  const handleTableSort = (column: any, sortOrder: string) => {
    let order = "";

    if (sortOrder === "asc") {
      setSortOrder("desc");
      order = "desc";
    } else if (sortOrder === "desc") {
      setSortOrder("asc");
      order = "asc";
    } else if (sortOrder === undefined) {
      setSortOrder("asc");
      order = "asc";
    }

    setOrderByName(column.dataIndex);

    const tempData = [...data];
    setSortedData(tempData.sort((a: any, b: any) => handleSort(a, b, order, column.dataIndex)));
    setCurrentPage(0);
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    if (apiUrl === "") return;

    const payload = {
      action: action,
      method: method,
      module: "Pages",
      subModule: "eramanPrivilegeCard_Faqs",
      type: "DEDICATED",
      application: "ERAMAN",
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "ERAMAN",
          apiUrl: apiUrl,
        },
      ],
      payload: data,
      status: status,
    };

    post("/management/publish", payload)
      .then((response) => {
        openToast({
          content: "Data Sent for Review",
          type: "success",
        });
        navigate("/dedicated/status-information?activeTab=pending");
      })
      .catch((err: any) => {
        openToast({
          content: "Failed to send data",
          type: "error",
        });
      });
  };

  async function sendDeleteAwardsRequest(reviewNotes: string) {
    try {
      await onSubmit(
        selectedRowData,
        `${eramanHost}/api/faq-s-list-data-crud-api/${selectedRowData?.id}`,
        "DELETE",
        "DELETE",
        "PENDING",
        reviewNotes
      );
    } catch (err) {}
  }

  return (
    <>
      <DataTable
        columns={columns.map((col) => ({
          ...col,
          onHeaderCell: (column: any) => (col.sorter ? { onClick: () => handleTableSort(col, sortOrder) } : {}),
        }))}
        multiSelect={false}
        className="table-clickable"
        tableData={
          currentPageData
            ? currentPageData?.map((d: any) => {
                return {
                  ...d,
                  title: decodeHTML(d.title),
                };
              })
            : []
        }
        isLoading={isLoading}
        exportFileName="CONTRACT"
        isPagination={true}
        recordsPerPage={5}
        onSearchChange={(searchText: string) => {
          setSearchValue(searchText);
          setCurrentPage(0);
        }}
        hideExport={true}
        isServerSearchApi={false}
        searchPlaceholderText={"Search Question"}
        onClick={(record) => {
          setSelectedRowData(record);
        }}
        additionalCTA={
          <div className="flex items-center space-x-2">
            <ActionDropdown
              value={selectedPublished}
              options={publishedData}
              onChange={handlePublishedChange}
              placeholder="Select Published"
              className="w-48"
            />
          </div>
        }
      />
      <div className="pagination">
        <Pagination
          defaultCurrent={1}
          current={currentPage + 1}
          // total={totalRecords}
          total={sortedData.length}
          pageSize={pageSize}
          onChange={(curPage, newPageSize) => {
            setCurrentPage(curPage - 1);
            if (newPageSize !== pageSize) {
              setPageSize(newPageSize);
              setCurrentPage(0);
            }
          }}
          showSizeChanger
          pageSizeOptions={["10", "20", "50", "100"]}
          // onChange={(curPage) => {
          //   setCurrentPage(curPage - 1);
          // }}
        />
      </div>

      <TableDrawer
        title={
          <Typography.Title
            level={4}
            className={"!mb-0"}
          >
            Detailed View
          </Typography.Title>
        }
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        fields={drilldownData[_get(drilldown, "subModule", "Faqs") as ModuleConfig]}
        data={drilldown?.[0]}
      />

      <ConfirmationModal
        type="delete"
        open={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onSubmit={sendDeleteAwardsRequest}
      />
    </>
  );
};

function handleSort(a: any, b: any, order: string, sortBy: string) {
  if (sortBy === "title") {
    const titleA = a[sortBy]?.toLowerCase() || "";
    const titleB = b[sortBy]?.toLowerCase() || "";

    if (order === "desc") {
      return titleA < titleB ? 1 : -1;
    } else if (order === "asc") {
      return titleA > titleB ? 1 : -1;
    } else {
      return 0;
    }
  }

  if (sortBy === "field_faqs_question") {
    const numberA = extractNumber(a[sortBy]);
    const numberB = extractNumber(b[sortBy]);

    if (order === "desc") {
      return numberA < numberB ? 1 : -1;
    } else if (order === "asc") {
      return numberA > numberB ? 1 : -1;
    } else {
      return 0;
    }
  }

  if (order === "desc") {
    return a[sortBy] < b[sortBy] ? 1 : -1;
  } else if (order === "asc") {
    return a[sortBy] > b[sortBy] ? 1 : -1;
  } else {
    return 0;
  }
}

function extractNumber(question: string): number {
  const match = question.match(/Q(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
}
